import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Grid,
    TablePagination,
    Typography,
    Stack,
    Chip
} from "@mui/material";
import { getPublishedArticles } from "../../Services/ApiCalls";
import type { Article } from "../../types";
import Seo from "../../Components/Seo";
import ResourceLearningSummary from "../../Components/ResourceLearningSummary";

/**
 * Determines if an article is a blog based on the excerpt value
 * Simply checks if the excerpt contains "BLOG" (case-insensitive)
 */
const isBlog = (excerpt: string | null | undefined): boolean => {
    if (!excerpt) return false;
    return excerpt.toLowerCase().includes('blog');
};

export default function Blogs() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [allArticles, setAllArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchArticles = async () => {
        try {
            setLoading(true);
            // Fetch a large batch to filter client-side
            const res = await getPublishedArticles(1, 1000);
            setAllArticles(res.articles || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    // Filter to show only blogs
    const blogs = useMemo(() => {
        return allArticles.filter(article => isBlog(article.excerpt));
    }, [allArticles]);

    // Paginate blogs
    const paginatedBlogs = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return blogs.slice(start, end);
    }, [blogs, page, rowsPerPage]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Seo
                title="Radiology Blogs"
                description="Read blog notes about radiology learning, academic practice, teaching, and visual medical education by Radiology Illustration."
                canonicalPath="/blogs"
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography 
                    variant="h3" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 700,
                        fontSize: {
                            xs: '1.5rem',
                            sm: '2rem',
                            md: '2.5rem',
                        }
                    }}
                >
                    Blogs
                </Typography>
            </Stack>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900, lineHeight: 1.8, mb: 3 }}>
                Blog posts collect educational reflections, study ideas, and teaching perspectives around radiology and medical illustration. They are written to support learning and should not be used as individual medical advice.
            </Typography>

            <ResourceLearningSummary variant="blogs" />

            {loading && <Typography>Loading blogs...</Typography>}

            {!loading && paginatedBlogs.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        No blogs published yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto', mt: 1, lineHeight: 1.7 }}>
                        New reflections and learning notes will appear here as they are published. You can continue browsing articles, infographics, and illustrations from the main navigation.
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {paginatedBlogs.map((blog) => (
                    <Grid key={blog.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Card 
                            sx={{ 
                                height: "100%", 
                                display: "flex", 
                                flexDirection: "column",
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3
                                }
                            }}
                        >
                            {blog.coverImage && (
                                <CardMedia 
                                    component="img" 
                                    image={blog.coverImage} 
                                    alt={blog.title} 
                                    sx={{ height: 200, objectFit: "cover" }} 
                                  />
                            )}
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        mb: 1,
                                        fontSize: {
                                            xs: '1rem',
                                            sm: '1.25rem',
                                        },
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {blog.title}
                                </Typography>
                                {blog.excerpt && (
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                            mb: 2, 
                                            flex: 1,
                                            fontSize: {
                                                xs: '0.8rem',
                                                sm: '0.875rem',
                                            },
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {blog.excerpt}
                                    </Typography>
                                )}
                                <Stack 
                                    direction="row" 
                                    spacing={1} 
                                    alignItems="center" 
                                    sx={{ mt: 'auto' }}
                                    flexWrap="wrap"
                                >
                                    <Chip 
                                        label="Blog" 
                                        color="primary" 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                    />
                                    {blog.publishedAt && (
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary"
                                            sx={{ 
                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {formatDate(blog.publishedAt)}
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                            <Box sx={{ p: 2, pt: 0 }}>
                                <Button 
                                    variant="outlined" 
                                    fullWidth
                                    component={Link}
                                    to={`/articles/${blog.slug}`}
                                >
                                    Read More
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {blogs.length > 0 && (
                <TablePagination
                    component="div"
                    count={blogs.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                />
            )}
        </Box>
    );
}
